import { useState } from 'react'
import Cta from '@/components/storefront/Cta'
import { money } from '@/lib/money'
import { tierTint } from '@/lib/tiers'
import { useAuth } from '@/context/AuthContext'
import { cn } from '@/lib/cn'
import type { Tier } from '@/types'

/**
 * Sidebar upsell for CR5 wallet tiers.
 *
 * The banner image is decoration only - every number and name here is read
 * from the tiers API, because tiers are admin-configurable rows and baking
 * "Gold" or a price into copy (or into the artwork) goes stale the moment an
 * admin edits one.
 */
export default function MemberPricingPromo({ tiers, className }: { tiers: Tier[]; className?: string }) {
  const { user } = useAuth()
  const [artworkMissing, setArtworkMissing] = useState(false)

  // Highest threshold wins - never assume a tier name or a fixed count.
  const best = [...tiers].sort((a, b) => (a.topup_threshold ?? 0) - (b.topup_threshold ?? 0)).at(-1)
  if (!best) return null

  const tint = tierTint(best.color)
  const signedIn = Boolean(user)

  return (
    <div className={cn('box overflow-hidden', className)}>
      {!artworkMissing && (
        <img
          src="/promo/member-pricing.png"
          alt=""
          className="aspect-[2/1] w-full object-cover"
          onError={() => setArtworkMissing(true)}
        />
      )}

      <div className="p-4">
        <p className="text-sm font-bold">Member pricing</p>

        <p className="mt-2 text-xs leading-relaxed text-foreground/60">
          <span className="font-semibold" style={{ color: tint.color }}>
            {best.name}
          </span>{' '}
          members pay the lowest flat rate on every product. Reach it at {money(best.topup_threshold)} lifetime
          top-up.
        </p>

        <Cta to={signedIn ? '/account/wallet' : '/register'} fullWidth arrow size="md" className="mt-3">
          {signedIn ? 'Top up now' : 'Join free'}
        </Cta>
      </div>
    </div>
  )
}
